import React, { useRef, useEffect, useState, useCallback } from 'react';
import './RichTextEditor.css';

/**
 * RichTextEditor Component
 * A reusable rich text editor component using contentEditable
 * Compatible with React 19
 * 
 * @param {string} value - The current content value (HTML string)
 * @param {Function} onChange - Callback function when content changes
 * @param {string} placeholder - Placeholder text
 * @param {number} rows - Number of visible rows (approximate)
 * @param {boolean} disabled - Whether the editor is disabled
 * @param {object} className - Additional CSS classes
 */
const RichTextEditor = ({ 
  value = '', 
  onChange, 
  placeholder = 'Write your content here...',
  rows = 6,
  disabled = false,
  className = ''
}) => {
  const editorRef = useRef(null);
  const isFocusedRef = useRef(false);
  const [isFocused, setIsFocused] = useState(false);
  const isInternalChangeRef = useRef(false);
  const lastValueRef = useRef('');
  const isInitialMountRef = useRef(true);
  const savedSelectionRef = useRef(null);

  // Save cursor position
  const saveSelection = useCallback(() => {
    if (!editorRef.current) return null;
    const selection = window.getSelection();
    if (selection.rangeCount === 0) return null;
    
    const range = selection.getRangeAt(0);
    const preSelectionRange = range.cloneRange();
    preSelectionRange.selectNodeContents(editorRef.current);
    preSelectionRange.setEnd(range.startContainer, range.startOffset);
    const start = preSelectionRange.toString().length;
    
    return {
      start,
      end: start + range.toString().length
    };
  }, []);

  // Restore cursor position
  const restoreSelection = useCallback((savedSel) => {
    if (!editorRef.current || !savedSel) return;
    
    const selection = window.getSelection();
    const range = document.createRange();
    
    let charIndex = 0;
    const nodeStack = [editorRef.current];
    let node;
    let foundStart = false;
    let stop = false;
    
    while (!stop && (node = nodeStack.pop())) {
      if (node.nodeType === Node.TEXT_NODE) {
        const nextCharIndex = charIndex + node.length;
        if (!foundStart && savedSel.start >= charIndex && savedSel.start <= nextCharIndex) {
          range.setStart(node, savedSel.start - charIndex);
          foundStart = true;
        }
        if (foundStart && savedSel.end >= charIndex && savedSel.end <= nextCharIndex) {
          range.setEnd(node, savedSel.end - charIndex);
          stop = true;
        }
        charIndex = nextCharIndex;
      } else {
        let i = node.childNodes.length;
        while (i--) {
          nodeStack.push(node.childNodes[i]);
        }
      }
    }
    
    selection.removeAllRanges();
    selection.addRange(range);
  }, []);

  // Update editor content when value prop changes (only from external source)
  useEffect(() => {
    if (!editorRef.current) return;
    
    const normalizedValue = value || '';
    
    // Handle initial mount - always set the value
    if (isInitialMountRef.current) {
      editorRef.current.innerHTML = normalizedValue;
      lastValueRef.current = normalizedValue;
      isInitialMountRef.current = false;
      return;
    }
    
    // Don't update if the change is from user input
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      lastValueRef.current = normalizedValue; // Update lastValueRef to match
      return;
    }
    
    // Only update if value actually changed externally and is different from current content
    const currentContent = editorRef.current.innerHTML || '';
    
    if (normalizedValue !== currentContent && normalizedValue !== lastValueRef.current) {
      const savedSelection = saveSelection();
      editorRef.current.innerHTML = normalizedValue;
      lastValueRef.current = normalizedValue;
      
      // Restore cursor position after a short delay (only if focused)
      if (savedSelection && isFocusedRef.current) {
        setTimeout(() => {
          restoreSelection(savedSelection);
        }, 0);
      }
    } else if (normalizedValue !== lastValueRef.current) {
      // Update lastValueRef even if content matches (for external updates)
      lastValueRef.current = normalizedValue;
    }
  }, [value, saveSelection, restoreSelection]);

  const handleInput = useCallback((e) => {
    if (!editorRef.current) return;
    
    isInternalChangeRef.current = true;
    const content = editorRef.current.innerHTML;
    lastValueRef.current = content;
    
    if (onChange) {
      onChange(content);
    }
  }, [onChange]);

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
    handleInput();
  };

  // Save selection when editor loses focus (e.g., when clicking toolbar button)
  const handleEditorMouseUp = useCallback(() => {
    if (editorRef.current) {
      const selection = saveSelection();
      // Only save if there's actually a selection (not just cursor position)
      if (selection) {
        const sel = window.getSelection();
        if (sel.rangeCount > 0 && sel.toString().length > 0) {
          savedSelectionRef.current = selection;
        }
      }
    }
  }, [saveSelection]);

  const execCommand = useCallback((command, value = null) => {
    if (!editorRef.current) {
      console.warn('Editor ref is null');
      return;
    }
    
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      if (!editorRef.current) return;
      
      // Restore selection immediately if we have one saved
      if (savedSelectionRef.current) {
        console.log('Restoring selection:', savedSelectionRef.current);
        restoreSelection(savedSelectionRef.current);
      }
      
      // Ensure editor is focused
      editorRef.current.focus();
      
      // Get current selection after restoration
      const selection = window.getSelection();
      
      // Verify we have a valid selection
      if (selection.rangeCount === 0) {
        // Try to restore again
        if (savedSelectionRef.current) {
          restoreSelection(savedSelectionRef.current);
        } else {
          console.warn('No selection available for command:', command);
          // For bold/italic/underline, if no selection, select the word at cursor
          if (['bold', 'italic', 'underline'].includes(command)) {
            document.execCommand('selectAll', false);
            const allSelection = window.getSelection();
            if (allSelection.rangeCount > 0) {
              const range = allSelection.getRangeAt(0);
              range.collapse(true);
              // Try to expand to word
              document.execCommand('selectAll', false);
            }
          }
        }
      }
      
      // Execute command
      try {
        const success = document.execCommand(command, false, value);
        console.log(`Command ${command} executed. Success: ${success}, Selection: "${selection.toString()}"`);
        if (!success) {
          console.warn(`Command ${command} returned false. Selection:`, selection.toString());
        } else {
          // Command succeeded - trigger input to update state
          handleInput();
        }
      } catch (error) {
        console.error(`Error executing command ${command}:`, error);
      }
      
      // Clear saved selection
      savedSelectionRef.current = null;
      
      // Keep focus on editor
      editorRef.current.focus();
    });
  }, [handleInput, restoreSelection]);

  const toolbarButtons = [
    { command: 'bold', icon: 'B', label: 'Bold' },
    { command: 'italic', icon: 'I', label: 'Italic' },
    { command: 'underline', icon: 'U', label: 'Underline' },
    { separator: true },
    { command: 'formatBlock', value: '<h1>', icon: 'H1', label: 'Heading 1' },
    { command: 'formatBlock', value: '<h2>', icon: 'H2', label: 'Heading 2' },
    { command: 'formatBlock', value: '<h3>', icon: 'H3', label: 'Heading 3' },
    { separator: true },
    { command: 'insertUnorderedList', icon: '•', label: 'Bullet List' },
    { command: 'insertOrderedList', icon: '1.', label: 'Numbered List' },
    { separator: true },
    { command: 'formatBlock', value: '<blockquote>', icon: '"', label: 'Quote' },
    { separator: true },
    { command: 'createLink', icon: '🔗', label: 'Link', isLink: true },
    { separator: true },
    { command: 'removeFormat', icon: '✕', label: 'Clear Formatting' },
  ];

  const handleLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      document.execCommand('createLink', false, url);
      editorRef.current?.focus();
      handleInput();
    }
  };

  // Calculate approximate height based on rows
  const editorHeight = rows * 24; // Approximate line height

  return (
    <div className={`rich-text-editor-wrapper ${className}`}>
      {/* Toolbar */}
      <div className="rich-text-toolbar">
        {toolbarButtons.map((btn, index) => {
          if (btn.separator) {
            return <div key={`sep-${index}`} className="toolbar-separator" />;
          }
          // Create unique key by combining command, value, and index
          const uniqueKey = `${btn.command}-${btn.value || ''}-${index}`;
          
          if (btn.isLink) {
            return (
              <button
                key={uniqueKey}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent blur
                  e.stopPropagation();
                  // Save selection before button click
                  if (editorRef.current) {
                    savedSelectionRef.current = saveSelection();
                  }
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleLink();
                }}
                className="toolbar-button"
                title={btn.label}
                disabled={disabled}
              >
                {btn.icon}
              </button>
            );
          }
          return (
            <button
              key={uniqueKey}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent blur
                e.stopPropagation();
                // Save selection before button click
                if (editorRef.current) {
                  savedSelectionRef.current = saveSelection();
                }
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                execCommand(btn.command, btn.value);
              }}
              className="toolbar-button"
              title={btn.label}
              disabled={disabled}
            >
              {btn.icon}
            </button>
          );
        })}
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable={!disabled}
        onInput={handleInput}
        onPaste={handlePaste}
        onMouseUp={handleEditorMouseUp}
        onKeyUp={handleEditorMouseUp}
        onFocus={() => { 
          isFocusedRef.current = true;
          setIsFocused(true);
        }}
        onBlur={() => { 
          isFocusedRef.current = false;
          setIsFocused(false);
          // Save selection when editor loses focus
          savedSelectionRef.current = saveSelection();
        }}
        className={`rich-text-editor ${isFocused ? 'focused' : ''} ${disabled ? 'disabled' : ''}`}
        style={{ minHeight: `${editorHeight}px` }}
        data-placeholder={placeholder}
        suppressContentEditableWarning
      />
    </div>
  );
};

export default RichTextEditor;
